import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import classnames from 'classnames';

import {CloseButton} from 'shells/button/close-button/close-button';

import {Button} from '../button/button';
import {colors} from '../colors/colors';
import {CancelCircleRegularIcon} from '../icon/svg/cancel-circle-regular-icon';
import {CheckedCircleRegularIcon} from '../icon/svg/checked-circle-regular-icon';
import {InfoCircleRegularIcon} from '../icon/svg/info-circle-regular-icon';
import {WarningRegularIcon} from '../icon/svg/warning-regular-icon';
import {Stack} from '../layout/stack/stack';
import {Body} from '../typography/body';

import styles from './toast-notifications.module.css';

export type ToastNotificationVariant = 'success' | 'error' | 'warning' | 'info' | 'neutral';

export interface ToastNotificationProps {
    title: string;
    subtitle?: string;
    variant?: ToastNotificationVariant;
    customIcon?: React.ReactNode;
    primaryAction?: {
        label: string;
        onClick: (e: React.SyntheticEvent<HTMLElement>) => void;
    };
    dismissAction: {
        label?: string;
        onDismiss: () => void;
    };
}

export function ToastNotification(props: ToastNotificationProps) {
    const {variant = 'neutral'} = props;

    const [isOpening, setIsOpening] = useState<boolean>(true);
    const [isClosing, setIsClosing] = useState<boolean>(false);

    const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autoCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Used to delay calling the dismiss action until the close animation completes
    const closeAnimationDelayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Used to store a stable dismissAction to avoid timmer resets on parent re-renders
    const dismissActionRef = useRef(props.dismissAction.onDismiss);
    const toastNotificationRef = useRef<HTMLLIElement | null>(null);

    const classes = classnames(styles['toast-notification'], {
        [styles['toast-notification--success']]: variant === 'success',
        [styles['toast-notification--error']]: variant === 'error',
        [styles['toast-notification--warning']]: variant === 'warning',
        [styles['toast-notification--info']]: variant === 'info',
        [styles['toast-notification--neutral']]: variant === 'neutral',
        [styles['toast-notification--on-animate-in']]: isOpening,
        [styles['toast-notification--on-animate-out']]: isClosing,
    });

    const icon = useMemo(() => {
        if (props.customIcon) {
            return props.customIcon;
        }

        switch (variant) {
            case 'error':
                //@ts-expect-error
                return <CancelCircleRegularIcon size={24} color={colors.roseDk} />;
            case 'warning':
                //@ts-expect-error
                return <WarningRegularIcon size={24} color={colors.orange} />;
            case 'info':
                //@ts-expect-error
                return <InfoCircleRegularIcon size={24} color={colors.blue} />;
            case 'neutral':
                //@ts-expect-error
                return <InfoCircleRegularIcon size={24} color={colors.grey} />;
            case 'success':
            default:
                //@ts-expect-error
                return <CheckedCircleRegularIcon size={24} color={colors.green} />;
        }
    }, [props.customIcon, variant]);

    const clearAutoCloseTimeout = useCallback(() => {
        if (autoCloseTimeoutRef.current) {
            clearTimeout(autoCloseTimeoutRef.current);
            autoCloseTimeoutRef.current = null;
        }
    }, []);

    const clearOpenTimeout = useCallback(() => {
        if (openTimeoutRef.current) {
            clearTimeout(openTimeoutRef.current);
            openTimeoutRef.current = null;
        }
    }, []);

    const clearCloseAnimationDelayTimeout = useCallback(() => {
        if (closeAnimationDelayTimeoutRef.current) {
            clearTimeout(closeAnimationDelayTimeoutRef.current);
            closeAnimationDelayTimeoutRef.current = null;
        }
    }, []);

    useEffect(() => {
        dismissActionRef.current = props.dismissAction.onDismiss;
    }, [props.dismissAction.onDismiss]);

    const handleClose = useCallback(
        (e?: React.SyntheticEvent<HTMLElement>) => {
            if (e?.stopPropagation) {
                e.stopPropagation();
            }

            clearAutoCloseTimeout();
            clearOpenTimeout();
            clearCloseAnimationDelayTimeout();

            // Store the notification height to be used for the out animation
            const toastNotification = toastNotificationRef.current;
            if (toastNotification) {
                toastNotification.style.setProperty(
                    '--toast-notification-height',
                    `${toastNotification.offsetHeight}px`
                );
            }

            setIsOpening(false);
            setIsClosing(true);

            closeAnimationDelayTimeoutRef.current = setTimeout(() => {
                dismissActionRef.current();
            }, 700);
        },
        [clearCloseAnimationDelayTimeout, clearAutoCloseTimeout, clearOpenTimeout]
    );

    useEffect(() => {
        // Clear opening state after enter animation completes
        openTimeoutRef.current = setTimeout(() => {
            setIsOpening(false);
            openTimeoutRef.current = null;
        }, 1600);

        autoCloseTimeoutRef.current = setTimeout(() => {
            handleClose();
        }, 8000);

        return () => {
            clearOpenTimeout();
            clearAutoCloseTimeout();
            clearCloseAnimationDelayTimeout();
        };
    }, [clearCloseAnimationDelayTimeout, clearAutoCloseTimeout, clearOpenTimeout, handleClose]);

    const handleMouseOver = useCallback(() => {
        if (isClosing) {
            return;
        }

        clearAutoCloseTimeout();
    }, [clearAutoCloseTimeout, isClosing]);

    const handleMouseLeave = useCallback(() => {
        if (isClosing) {
            return;
        }

        clearCloseAnimationDelayTimeout();
        autoCloseTimeoutRef.current = setTimeout(() => {
            handleClose();
        }, 7000);
    }, [clearCloseAnimationDelayTimeout, handleClose, isClosing]);

    const handleCardClick = useCallback(
        (e: React.SyntheticEvent<HTMLElement>) => {
            handleClose(e);

            if (props.primaryAction?.onClick) {
                props.primaryAction.onClick(e);
            }
        },
        [handleClose, props.primaryAction]
    );

    const handlePrimaryActionClick = useCallback(
        (e: React.SyntheticEvent<HTMLElement>) => {
            e.stopPropagation();
            handleCardClick(e);
        },
        [handleCardClick]
    );

    const handleDismissActionClick = useCallback(
        (e: React.SyntheticEvent<HTMLElement>) => {
            handleClose(e);
        },
        [handleClose]
    );

    return (
        <li
            ref={toastNotificationRef}
            className={classes}
            onClick={handleCardClick}
            onMouseOver={handleMouseOver}
            onMouseLeave={handleMouseLeave}
        >
            <Stack isHorizontal={true} alignItems='start' spacing={8}>
                {icon}
                <Stack spacing={16}>
                    <Stack spacing={4}>
                        <Body color='dark' isBold={true}>
                            {props.title}
                        </Body>
                        <Body size='small'>{props.subtitle}</Body>
                    </Stack>
                    <Stack isHorizontal={true} spacing={16}>
                        {props.primaryAction && (
                            <Button
                                variant='text-primary'
                                size='small'
                                onClick={handlePrimaryActionClick}
                                noPadding={true}
                            >
                                {props.primaryAction.label}
                            </Button>
                        )}
                        <Button
                            variant='text-secondary'
                            size='small'
                            onClick={handleDismissActionClick}
                            noPadding={true}
                        >
                            {props.dismissAction.label || 'Dismiss'}
                        </Button>
                    </Stack>
                </Stack>
            </Stack>
            <CloseButton onClick={handleClose} />
        </li>
    );
}
